import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  colorForCategory,
  eventDisplayTitle,
  formatDateLabel,
  labelForCategory,
} from './constants';

export default function MoreEventsPopover({
  open,
  dateIso,
  events,
  anchorRect,
  draggingAway,
  draggingEventId,
  onClose,
  onEventClick,
  onEventContextMenu,
  onEventDragStart,
  onEventDragEnd,
}) {
  const { t } = useTranslation(['planning', 'common']);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !panelRef.current || !anchorRect || draggingAway) return;

    const panel = panelRef.current;
    const gap = 8;
    let left = anchorRect.left;
    let top = anchorRect.bottom + gap;
    const width = panel.offsetWidth || 260;
    const height = panel.offsetHeight || 200;

    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    if (left < 8) left = 8;
    if (top + height > window.innerHeight - 8) {
      top = anchorRect.top - height - gap;
    }
    if (top < 8) top = 8;

    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }, [open, anchorRect, draggingAway, events]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className={`month-more-popover${draggingAway ? ' is-dragging-away' : ''}`}
      role="dialog"
      aria-labelledby="more-events-heading"
      style={{ left: 0, top: 0 }}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-border">
        <p id="more-events-heading" className="text-xs font-bold text-primary">
          {t('allEvents')}
        </p>
        <button
          type="button"
          className="p-0.5 text-text-secondary hover:text-primary rounded transition-colors"
          aria-label={t('common:close')}
          onClick={onClose}
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>
      <p className="px-3 pt-2 text-[10px] text-text-secondary">
        {dateIso ? formatDateLabel(dateIso) : ''}
      </p>
      <div className="month-more-list">
        {events.map((ev) => {
          const color = colorForCategory(ev.category);
          const title = eventDisplayTitle(ev);
          return (
            <button
              key={ev.segmentKey || ev.id}
              type="button"
              className={`month-more-event-item event-${color}${
                String(draggingEventId) === String(ev.id) ? ' is-dragging' : ''
              }`}
              draggable
              onClick={() => onEventClick?.(ev)}
              onContextMenu={(e) => {
                e.preventDefault();
                onEventContextMenu?.(ev, e.clientX, e.clientY);
              }}
              onDragStart={(e) => onEventDragStart?.(ev, e, true)}
              onDragEnd={onEventDragEnd}
            >
              <span className="more-event-title">{title}</span>
              <span className="more-event-meta">
                {ev.start} – {ev.end} · {labelForCategory(ev.category)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
